import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../api/ApiProvider";
import {
  FiskalyTssResponseDto,
  GetAllTssOrderByEnum,
  GetDashboardStats200Response,
  FiskalyClientResponseDto,
  GetAllClientsOrderByEnum,
  TransactionsResponseDto,
  ExportTransactionsResponseDto,
} from "../../api-client";
import { taxationKeys } from "./taxation.keys";

/**
 * Query to fetch all TSS records with optional sorting
 * @param orderBy - Order results by: description, state, or time_creation
 * @returns UseQueryResult<FiskalyTssResponseDto[], Error>
 */
export const useGetAllTss = (orderBy?: GetAllTssOrderByEnum) => {
  const { taxation } = useApi();

  return useQuery<FiskalyTssResponseDto[]>({
    queryKey: taxationKeys.tssAll.list({ orderBy }),
    queryFn: () => taxation.getAllTss(orderBy),
    staleTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
};

export const useGetAllClients = (orderBy?: GetAllClientsOrderByEnum) => {
  const { taxation } = useApi();

  return useQuery<FiskalyClientResponseDto[]>({
    queryKey: taxationKeys.clientAll.list({ orderBy }),
    queryFn: () => taxation.getAllClients(orderBy),
    staleTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
};

/**
 * Query to fetch all export records
 * @returns UseQueryResult<ShowExportDto[], Error>
 */
export const useGetAllTssExports = (tssId: string, limit: number, offset: number) => {
  const { taxation } = useApi();

  return useQuery<ExportTransactionsResponseDto>({
    queryKey: taxationKeys.exports.forTss(tssId),
    queryFn: () => taxation.getAllTssExports(tssId, limit, offset),
    enabled: !!tssId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
};

/**
 * Query to fetch dashboard statistics
 * @returns UseQueryResult with enhanced dashboard stats
 */
export const useGetDashboardStats = () => {
  const { taxation } = useApi();

  return useQuery({
    queryKey: taxationKeys.dashboard.all(),
    queryFn: () => taxation.getDashboardStats(),
    staleTime: 2 * 60 * 1000, // 2 minutes - shorter for fresh dashboard data
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });
};

/**
 * Query to fetch a single TSS by ID
 * @param tssId - TSS ID to fetch
 * @returns UseQueryResult<FiskalyTssResponseDto, Error>
 */
export const useGetTssById = (tssId: string) => {
  const { taxation } = useApi();

  return useQuery<FiskalyTssResponseDto>({
    queryKey: taxationKeys.tss.byId(tssId),
    queryFn: () => taxation.getTssById(tssId),
    enabled: !!tssId,
    staleTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Query to fetch all transactions for a client
 * @param tssId - TSS ID the client belongs to
 * @param clientId - Client ID to fetch transactions for
 * @param limit - Number of transactions per page (default: 10)
 * @param offset - Offset for pagination (default: 0)
 * @returns UseQueryResult<TransactionsResponseDto, Error>
 */
export const useGetClientTransactions = (
  tssId: string,
  clientId: string,
  limit: number = 10,
  offset: number = 0,
) => {
  const { taxation } = useApi();

  return useQuery<TransactionsResponseDto>({
    queryKey: taxationKeys.transactions.forClient(tssId, clientId, offset),
    queryFn: () =>
      taxation.getAllClientTransactions(tssId, clientId, limit, offset),
    enabled: !!tssId && !!clientId, // Only fetch when both IDs are available
    staleTime: 0, // Always fetch fresh data
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Always refetch when component mounts
  });
};
